import { useEffect, useState } from "react";
import { AGE_BUCKETS, useCoursesList } from "@/hooks/useCoursesList";
import { CalendarCheck, CalendarDays, ChevronDown } from "lucide-react";
import CourseCard from "./CourseCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { cn } from "@/lib/utils";

interface CoursesListProps {
  clubId: string | null;
  trainerId?: string;
}

const CoursesList = ({ clubId, trainerId = "all" }: CoursesListProps) => {
  const { groupedByDate, isLoading, isError, filters, setFilters, bucketCounts, groupTicketId } =
    useCoursesList(clubId, trainerId);

  const [openDate, setOpenDate] = useState<string>("");

  // Если текущий открытый день больше не существует (смена возрастного бакета
  // или клуба убрала этот день из списка) — открываем ближайший доступный.
  useEffect(() => {
    if (groupedByDate.length === 0) return;
    const stillExists = groupedByDate.some((g) => g.key === openDate);
    if (!openDate || !stillExists) {
      setOpenDate(groupedByDate[0].key);
    }
  }, [groupedByDate, openDate]);

  if (isError) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm text-destructive">Не удалось загрузить список секций</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-1.5">
        {AGE_BUCKETS.map((bucket) => {
          const active = filters.ageBucket === bucket.id;
          const disabled = bucketCounts[bucket.id] === 0;
          return (
            <button
              key={bucket.id}
              onClick={() => setFilters((prev) => ({ ...prev, ageBucket: bucket.id }))}
              disabled={disabled}
              className={`flex items-center justify-center rounded-lg border-2 px-2 py-2.5 text-sm font-medium leading-tight transition ${
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : disabled
                    ? "border-gray-100 bg-gray-50 text-gray-300"
                    : "border-gray-200 bg-white text-gray-800 hover:border-gray-400"
              }`}
            >
              {bucket.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : groupedByDate.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <CalendarDays className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-1 text-lg font-medium text-gray-900">Нет занятий</h3>
          <p className="text-sm text-gray-500">
            Для выбранного филиала и фильтров нет доступных занятий на ближайшие 4 недели
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {groupedByDate.map(({ key, dateLabel, dayOfWeekLabel, items }) => {
            const hasBooking = items.some(({ cls }) => cls.already_booked);
            return (
              <Accordion
                type="single"
                collapsible
                key={key}
                value={openDate}
                onValueChange={setOpenDate}
              >
                <AccordionItem
                  value={key}
                  className="group relative w-full overflow-hidden rounded-lg border bg-white"
                >
                  <AccordionTrigger
                    className={cn(
                      "w-full px-4 py-3 transition-all duration-200 hover:no-underline sm:px-6 sm:py-4",
                      "data-[state=open]:bg-muted/30",
                      "[&[data-state=open]>div]:text-primary [&[data-state=open]_.icon]:text-primary",
                      "[&[data-state=open]_.chevron]:rotate-180",
                      "group hover:bg-muted/30",
                      "bg-muted/50 [&>svg]:hidden"
                    )}
                  >
                    <div className="flex w-full items-center gap-2 transition-colors duration-200 sm:gap-3">
                      <div className="flex items-center gap-2">
                        <div className="rounded-full bg-primary/10 p-2">
                          <CalendarDays className="icon h-5 w-5 transition-colors duration-200" />
                        </div>
                        <div className="flex min-w-[92px] flex-col items-start">
                          <span className="font-medium">{dateLabel}</span>
                          <span className="text-sm capitalize text-gray-500">{dayOfWeekLabel}</span>
                        </div>
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        {hasBooking && (
                          <Badge variant="secondary" className="gap-1.5">
                            <CalendarCheck className="h-3 w-3" />
                            <span className="hidden sm:inline">Есть запись</span>
                          </Badge>
                        )}
                        <span className="hidden text-sm text-gray-500 transition-colors duration-200 group-hover:text-gray-900 sm:inline">
                          {items.length}{" "}
                          {items.length === 1
                            ? "занятие"
                            : items.length < 5
                              ? "занятия"
                              : "занятий"}
                        </span>
                        <div className="rounded-full bg-gray-50 p-2 transition-colors duration-200 group-hover:bg-gray-100">
                          <ChevronDown className="chevron h-4 w-4 text-gray-500 transition-all duration-300 group-hover:text-gray-900" />
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <div className="space-y-2 border-t px-4 pb-4 pt-3 sm:px-6">
                      {items.map(({ cls, course }) => (
                        <CourseCard
                          key={cls.appointment_id}
                          course={course}
                          classInstance={cls}
                          ticketId={groupTicketId}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoursesList;
